import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Volume2, VolumeX } from "lucide-react";
import { Eyebrow, MaskText, Reveal } from "@/components/motion";
import { INSTAGRAM, REELS, formatINR, productUrl } from "@/data/catalog";
import { useLiveProduct } from "@/lib/live";
import { EASE } from "@/lib/scroll";

const Reel = ({ r: base, i }) => {
  const r = useLiveProduct(base);
  const vid = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = vid.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.35 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (vid.current) vid.current.muted = muted;
  }, [muted]);

  return (
    <div className={`w-[64vw] shrink-0 snap-center sm:w-[260px] lg:w-auto ${i % 2 ? "lg:mt-20" : ""}`}>
      <motion.div
        data-testid={`ugc-reel-card-${i}`}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.9, ease: EASE, delay: i * 0.08 }}
        className="group relative aspect-[9/16] overflow-hidden rounded-[22px] border border-white/10 bg-card"
      >
        <video ref={vid} src={r.src} poster={r.poster} muted loop playsInline preload="none" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />
        <div className="absolute inset-x-3 top-3 flex items-center justify-between">
          <span className="flex items-center gap-1.5 font-mono text-[11px] text-cream"><Instagram className="h-3.5 w-3.5" /> acevanity1</span>
          <button
            data-testid={`ugc-reel-mute-${i}`}
            aria-label={muted ? "Unmute" : "Mute"}
            onClick={() => setMuted((m) => !m)}
            className="grid h-9 w-9 place-items-center rounded-full bg-black/40 text-cream backdrop-blur-md transition-colors hover:bg-champagne hover:text-ink"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
        <a
          href={productUrl(r.handle)}
          data-testid={`ugc-reel-product-${i}`}
          className="absolute inset-x-3 bottom-3 flex items-center gap-3 rounded-2xl border border-white/15 bg-black/45 p-3 backdrop-blur-md transition-colors hover:border-champagne/60"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-cream">{r.name}</span>
            <span className="block font-mono text-xs text-champagne">{r.available === false ? "Sold out" : formatINR(r.price)}</span>
          </span>
          <ArrowUpRight className="h-4 w-4 shrink-0 text-champagne" />
        </a>
      </motion.div>
    </div>
  );
};

export const Reels = () => (
  <section id="reels" data-testid="ugc-reels-section" className="relative overflow-hidden bg-ink-2 py-24 lg:py-36">
    <div className="mx-auto mb-14 grid max-w-[1440px] gap-6 px-5 sm:px-8 lg:grid-cols-12 lg:items-end lg:px-12">
      <div className="lg:col-span-7">
        <Eyebrow>07 — In motion</Eyebrow>
        <h2 className="mt-5 font-display text-5xl font-light leading-[0.95] text-cream sm:text-6xl lg:text-7xl">
          <MaskText lines={["Lashes that", <em key="e" className="italic text-champagne">move with you.</em>]} />
        </h2>
      </div>
      <Reveal className="lg:col-span-5" delay={0.1}>
        <p className="text-sm leading-relaxed text-stone sm:text-base">Real close-ups from the WINK lash bar. Tap the speaker for sound, tap the tag to shop the exact pair.</p>
        <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="reels-follow-instagram" className="btn-ghost mt-6">
          <Instagram className="h-4 w-4" /> Watch more on Instagram
        </a>
      </Reveal>
    </div>
    <div className="no-scrollbar mx-auto flex max-w-[1440px] snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-24 sm:px-8 lg:grid lg:grid-cols-5 lg:gap-5 lg:overflow-visible lg:px-12">
      {REELS.map((r, i) => (
        <Reel key={r.id} r={r} i={i} />
      ))}
    </div>
  </section>
);
